const HEADING_RE = /<h([1-6])\b([^>]*)>([\s\S]*?)<\/h\1>/gi;
const IMG_RE = /<img\b([^>]*)>/gi;
const ATTR_RE = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>`]+)))?/g;
const TAG_RE = /<[^>]+>/g;
const WHITESPACE_RE = /(?:\s|&nbsp;|&#160;|&#xA0;)+/g;

const STOPWORDS = new Set([
  "a", "an", "the", "in", "on", "of", "at", "to", "for",
  "and", "or", "with", "from", "by", "as", "your", "our",
  "my", "is", "are", "be",
]);

const normalizeText = (s) =>
  String(s || "")
    .replace(TAG_RE, " ")
    .replace(WHITESPACE_RE, " ")
    .trim();

const tidyInnerWhitespace = (inner) =>
  inner
    .replace(/(&nbsp;|&#160;|&#xA0;)/g, " ")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s*\n\s*/g, " ")
    .trim();

const stem = (t) => (t.length > 5 ? t.slice(0, 5) : t);

const contentTokens = (s) =>
  normalizeText(s)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .filter((t) => !STOPWORDS.has(t));

const tokenMatchesAnchor = (token, anchorStems) => {
  if (anchorStems.has(stem(token))) return true;
  for (const a of anchorStems) {
    if (a.length < 4) continue;
    if (a.startsWith(token) || token.startsWith(a)) return true;
  }
  return false;
};

const isVariantOfAnchor = (text, anchorText) => {
  if (!anchorText) return false;
  const anchorStems = new Set(contentTokens(anchorText).map(stem));
  const tokens = contentTokens(text);
  if (tokens.length === 0 || anchorStems.size === 0) return false;
  return tokens.every((t) => tokenMatchesAnchor(t, anchorStems));
};

const parseAttrs = (attrStr) => {
  const out = {};
  if (!attrStr) return out;
  let m;
  ATTR_RE.lastIndex = 0;
  while ((m = ATTR_RE.exec(attrStr)) !== null) {
    const name = m[1].toLowerCase();
    const value = m[2] ?? m[3] ?? m[4] ?? "";
    out[name] = value;
  }
  return out;
};

const serializeAttrs = (attrs) =>
  Object.entries(attrs)
    .map(([k, v]) => (v === "" ? ` ${k}` : ` ${k}="${String(v).replace(/"/g, "&quot;")}"`))
    .join("");

// Editor-emitted <img> tags often lack alt / loading / decoding. Inject safe
// defaults without overriding anything the author already set. Strips on*
// event handlers as a basic defense against pasted-in HTML.
const hardenImages = (html) =>
  html.replace(IMG_RE, (_, rawAttrs) => {
    const attrs = parseAttrs(rawAttrs);
    Object.keys(attrs).forEach((k) => {
      if (k.startsWith("on")) delete attrs[k];
    });
    if (attrs.src && /^javascript:/i.test(attrs.src)) delete attrs.src;
    if (attrs.alt === undefined) attrs.alt = "";
    if (attrs.loading === undefined) attrs.loading = "lazy";
    if (attrs.decoding === undefined) attrs.decoding = "async";
    return `<img${serializeAttrs(attrs)}>`;
  });

// Internal heading pass. `demoteH1` collapses author-inserted H1s into H2s
// (because MOST public pages already render their own H1 in the page chrome).
// `keepFirstH1` is the exception for templates that render no H1 of their own:
// the first author H1 is promoted to be the page's H1 and later ones still
// demote, so the page gets exactly one.
function processHeadings(html, { h1 = "", demoteH1 = true, keepFirstH1 = false } = {}) {
  const keptH2Texts = [];
  let h1Kept = false;

  return html.replace(HEADING_RE, (_, level, attrs, inner) => {
    const cleanedInner = tidyInnerWhitespace(inner);
    const text = normalizeText(cleanedInner);

    if (!text) return "";

    let effectiveLevel = Number(level);
    if (effectiveLevel === 1) {
      if (keepFirstH1 && !h1Kept) h1Kept = true;
      else if (demoteH1) effectiveLevel = 2;
    }

    if (effectiveLevel === 2) {
      const demote = () =>
        `<p${attrs}><strong>${cleanedInner}</strong></p>`;

      if (h1 && isVariantOfAnchor(text, h1)) return demote();

      for (const prev of keptH2Texts) {
        if (isVariantOfAnchor(text, prev) || isVariantOfAnchor(prev, text)) {
          return demote();
        }
      }

      keptH2Texts.push(text);
    }

    return `<h${effectiveLevel}${attrs}>${cleanedInner}</h${effectiveLevel}>`;
  });
}

export function sanitizeRentalContent(html, { h1 = "" } = {}) {
  if (!html || typeof html !== "string") return "";
  return hardenImages(processHeadings(html, { h1, demoteH1: true }));
}

// Blog posts already render their title as <h1> in BlogDetails; demote any
// author-inserted H1s to H2 and harden inline images. Skips the H2-vs-H1
// dedupe pass because blog bodies aren't keyword-anchored the same way.
export function sanitizeBlogContent(html) {
  if (!html || typeof html !== "string") return "";
  return hardenImages(processHeadings(html, { h1: "", demoteH1: true }));
}

// Legal pages (/legal/*) are the one template that renders NO H1 of its own —
// the whole page body is CMS content. Running them through
// sanitizeRentalContent demoted the document's own H1 ("TERMS AND CONDITIONS")
// to H2 and left the page with zero H1s. Keep the first author H1 as the page
// H1; later ones still demote so there is exactly one.
export function sanitizeLegalContent(html) {
  if (!html || typeof html !== "string") return "";
  return hardenImages(processHeadings(html, { h1: "", demoteH1: true, keepFirstH1: true }));
}

export default sanitizeRentalContent;
